import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom'
import { useEffect } from 'react'
import { useAuth } from './modules/auth/hooks/useAuth'
import ConditionalLandingRoute from './components/ConditionalLandingRoute'
import Dashboard from './pages/dashboard/Dashboard'
import { TripsLayout } from './pages/trips/TripsLayout'
import { TripsEmptyState } from './pages/trips/TripsEmptyState'
import { TripDetailContent } from './pages/trips/TripDetail'
import TripDetail from './pages/trips/TripDetail'
import Login from './pages/auth/Login'
import SignUp from './pages/auth/SignUp'
import ForgotPassword from './pages/auth/ForgotPassword'
import ResetPassword from './pages/auth/ResetPassword'
import VerifyEmail from './pages/auth/VerifyEmail'
import AuthCallback from './pages/auth/AuthCallback'
import OnboardingPage from './pages/OnboardingPage'
import JoinTrip from './pages/JoinTrip'
import UserProfile from './pages/UserProfile'
import ItineraryBuilderPage from './pages/ItineraryBuilderPage'
import ContactUs from './pages/ContactUs'
import TermsOfService from './pages/TermsOfService'
import PrivacyPolicy from './pages/PrivacyPolicy'
import Documentation from './pages/Documentation'
import AboutUs from './pages/AboutUs'
import PricingPage from './pages/PricingPage'
import BetaInvitations from './pages/BetaInvitations'
import ShareLinkView from './pages/ShareLinkView'
import WaypointShareView from './pages/WaypointShareView'
import CalendarPage from './pages/CalendarPage'
import NotificationsPage from './pages/NotificationsPage'
import { GaragePage } from './pages/garage/GaragePage'
import { PhotosPage } from './pages/photos/PhotosPage'
import { ProtectedRoute, PublicRoute, AdminRoute } from './modules/auth'
import { isRestorablePath } from './shared/utils/auth'
import AdminPage from './pages/admin/AdminPage'
import { WaveLoader } from './ui/components'
import { TooltipProvider } from './ui/components/primitives/tooltip'
import { NotificationsPanelProvider } from './contexts/NotificationsPanelContext'
import { RangerPanelProvider } from './contexts/RangerPanelContext'
import { AppNavProvider } from './contexts/AppNavContext'
import { ThemeProviderWrapper } from './contexts/ThemeContext'
import { NotificationsPanel } from './ui/components/notifications/NotificationsPanel'
import { RangerPanel } from './modules/ai'

function RoutePersistence() {
	const location = useLocation()
	const navigate = useNavigate()
	const { isLoading, isAuthenticated } = useAuth()

	// Persist on path changes (skip login/signup so redirect targets are preserved)
	useEffect(() => {
		if (!isLoading) {
			try {
				const path = location.pathname + location.search
				if (isRestorablePath(path)) {
					sessionStorage.setItem('last_pathname', path)
				}
			} catch {}
		}
	}, [location.pathname, location.search, isLoading])

	// Restore on initial mount if we unexpectedly land on root
	useEffect(() => {
		if (isLoading) return
		try {
			const last = sessionStorage.getItem('last_pathname')
			const current = location.pathname
			const isPublic = !isRestorablePath(current)
			const canRestorePath = isRestorablePath(last) && last !== current
			if (isAuthenticated && isPublic && canRestorePath) {
				navigate(last, { replace: true })
			}
		} catch {}
	}, [isLoading, isAuthenticated])

	return null
}

function App() {
  const { isLoading, isAuthenticated } = useAuth()

  // Show loading spinner while auth is initializing
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <WaveLoader />
      </div>
    )
  }

  return (
    <ThemeProviderWrapper>
      <TooltipProvider delayDuration={300}>
        <AppNavProvider>
        <NotificationsPanelProvider>
        <RangerPanelProvider>
        <RoutePersistence />
        <Routes>
        {/* Public routes - landing at /; signup/login open */}
        <Route path="/" element={<ConditionalLandingRoute />} />
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/signup" element={<PublicRoute><SignUp /></PublicRoute>} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
        <Route path="/auth/verify" element={<VerifyEmail />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/users/:userId" element={<ProtectedRoute><UserProfile /></ProtectedRoute>} />
        <Route path="/join" element={<JoinTrip />} />
        <Route path="/share/waypoint/:token" element={<WaypointShareView />} />
        <Route path="/share/:token" element={<ShareLinkView />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/docs" element={<Documentation />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/pricing" element={<PricingPage />} />
        
        {/* Protected routes */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/explore/trip/:tripid" element={<ProtectedRoute><ItineraryBuilderPage /></ProtectedRoute>} />
        <Route path="/explore/waypoint/:waypointid" element={<ProtectedRoute><ItineraryBuilderPage /></ProtectedRoute>} />
        <Route path="/explore/library" element={<Navigate to="/explore" replace />} />
        <Route path="/explore/trips" element={<ProtectedRoute><ItineraryBuilderPage /></ProtectedRoute>} />
        <Route path="/explore" element={<ProtectedRoute><ItineraryBuilderPage /></ProtectedRoute>} />
        <Route path="/trips" element={<ProtectedRoute><TripsLayout /></ProtectedRoute>}>
          <Route index element={<TripsEmptyState />} />
          <Route path="new" element={<TripsEmptyState />} />
          <Route path=":tripId" element={<TripDetailContent />} />
        </Route>
        <Route path="/garage" element={<ProtectedRoute><GaragePage /></ProtectedRoute>} />
        <Route path="/garage/:userId" element={<ProtectedRoute><GaragePage /></ProtectedRoute>} />
        <Route path="/photos" element={<ProtectedRoute><PhotosPage /></ProtectedRoute>} />
        <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
        <Route path="/beta-invitations" element={<ProtectedRoute><BetaInvitations /></ProtectedRoute>} />
        <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />

        {/* Test routes */}
        
        {/* Guest routes - no authentication required */}
        <Route path="/trips/:tripId/guest" element={<TripDetail />} />
        </Routes>
        {isAuthenticated && <NotificationsPanel />}
        {isAuthenticated && <RangerPanel />}
        </RangerPanelProvider>
        </NotificationsPanelProvider>
        </AppNavProvider>
      </TooltipProvider>
    </ThemeProviderWrapper>
  )
}

export default App
